import React from "react";
import { motion } from "framer-motion";
import cvData from "../data/cvData";
import projects from "../data/projectsData";
import "../components/Section.css";
import FeaturedSlider from "../components/FeaturedSlider";
import Seo from "../components/Seo";
import { fadeUp, staggerContainer, staggerItem, viewportOnce, easing } from "../utils/motionVariants";
import "./AboutPage.css";

export default function AboutPage() {
  const { summary, experience, education, thesis, location, contacts, references } = cvData;
  const featured = projects.slice(0, 4);
  const leadRole = experience[0] || {};
  const experienceHighlights = experience.slice(0, 3);

  const facts = [
    { label: "Current Role", value: `${leadRole.role || ""} @ ${leadRole.company || ""}` },
    { label: "Education", value: education.degree },
    { label: "Location", value: location },
  ];

  const stats = [
    { label: "Professional Roles", value: `${experience.length}+` },
    { label: "Products Delivered", value: `${projects.length}+` },
    { label: "References", value: `${references.length}` },
  ];

  return (
    <section id="about" className="section section-lilac about-section">
      <Seo
        title="About"
        path="/about"
        description={`About ${cvData.name} - ${summary}`}
      />
      <motion.div
        className="about-hero"
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={staggerItem}>
          <p className="eyebrow">About</p>
          <h2>Design-forward engineer crafting thoughtful products.</h2>
          <p>{summary}</p>
          <motion.div
            className="about-stats"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            {stats.map((stat) => (
              <motion.div
                key={stat.label}
                variants={staggerItem}
                whileHover={{ y: -4, scale: 1.04 }}
                transition={{ duration: 0.25, ease: easing }}
              >
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
        <motion.div
          className="about-card"
          variants={staggerItem}
          whileHover={{ y: -6, boxShadow: "0 24px 50px rgba(15, 23, 42, 0.12)" }}
          transition={{ duration: 0.3, ease: easing }}
        >
          <h3>Quick Facts</h3>
          <ul>
            {facts.map((fact, idx) => (
              <motion.li
                key={fact.label}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + idx * 0.08, duration: 0.35 }}
              >
                <span>{fact.label}</span>
                <strong>{fact.value}</strong>
              </motion.li>
            ))}
            <motion.li
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + facts.length * 0.08, duration: 0.35 }}
            >
              <span>Preferred Contact</span>
              <a href={`mailto:${contacts.email}`}>{contacts.email}</a>
            </motion.li>
          </ul>
        </motion.div>
      </motion.div>

      <motion.div
        className="about-highlights"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
      >
        {experienceHighlights.map((role) => (
          <motion.article
            key={role.company}
            className="highlight-card"
            variants={staggerItem}
            whileHover={{ y: -8, scale: 1.015 }}
            transition={{ duration: 0.25, ease: easing }}
          >
            <div className="badge">{role.period}</div>
            <h4>{role.role}</h4>
            <p className="muted">
              {role.company} &middot; {role.location}
            </p>
            <p>{role.details}</p>
          </motion.article>
        ))}
      </motion.div>

      <motion.div
        className="thesis-card"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        whileHover={{ y: -4, boxShadow: "0 24px 50px rgba(15, 23, 42, 0.12)" }}
        transition={{ duration: 0.3, ease: easing }}
      >
        <div>
          <p className="eyebrow">Signature Case</p>
          <h3>{thesis.title}</h3>
          <p>{thesis.description}</p>
          <p className="muted">
            Supervised by {thesis.supervisors}. {thesis.completed}.
          </p>
        </div>
        <div className="thesis-meta">
          <span>Domain</span>
          <strong>AI + IoT + Sustainability</strong>
          {thesis.link && (
            <a href={`https://${thesis.link}`} target="_blank" rel="noreferrer">
              View project
            </a>
          )}
        </div>
      </motion.div>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
      >
        <FeaturedSlider projects={featured} title="Featured Work" />
      </motion.div>
    </section>
  );
}
